import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import Component from '@glimmer/component';

import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import willDestroy from 'webapp/modifiers/will-destroy';
import type { MetricItem } from 'webapp/components/form/metric-select/popup/index';
import FormMetricSelectPopupListItemComponent from 'webapp/components/form/metric-select/popup/list-item';

interface Args {
	metric: MetricItem | null;
	options: MetricItem[];
	onSelect: (item: MetricItem) => void;
	onIncrementalLoad: (page?: number, pageSize?: number) => void;
	theme?: string;
	hideFavoriteBtn?: boolean;
	hideConnectBtn?: boolean;
	originForMetricFavorite?: string;
}

export default class FormMetricSelectPopupListComponent extends Component<Args> {
	itemsNode: HTMLElement | null = null;
	contentNodeHeight = 0;

	incrementalLoad(): boolean {
		const element = this.itemsNode!;

		if (element.clientHeight + element.scrollTop + 100 > element.scrollHeight) {
			this.args.onIncrementalLoad();
			return true;
		}
		return false;
	}

	@action
	debounceIncrementalLoad(): void {
		debounce(this, this.incrementalLoad, 150);
	}

	@action
	calculateItemsHeight(): void {
		const itemsNode = this.itemsNode!,
			contentNode = itemsNode.closest<HTMLElement>('.content')!,
			contentNodeHeight = parseInt(contentNode?.style.maxHeight || '0');

		if (this.contentNodeHeight !== contentNodeHeight) {
			const filterNode = contentNode?.querySelector<HTMLElement>('.filter-line'),
				itemsNodeHeight = contentNodeHeight - (filterNode?.offsetHeight || 0);

			itemsNode.style.maxHeight = `${itemsNodeHeight}px`;
			this.contentNodeHeight = contentNodeHeight;
		}
	}

	@action
	setupView(node: HTMLElement): void {
		this.itemsNode = node;
		this.calculateItemsHeight();

		// for high resolutions, we might need to load extra immediately
		if (this.incrementalLoad()) {
			this.debounceIncrementalLoad();
		}

		// add support for incremental load
		node.addEventListener('scroll', this.debounceIncrementalLoad);
	}

	@action
	teardownView(): void {
		this.itemsNode?.removeEventListener('scroll', this.debounceIncrementalLoad);
	}
	<template>
		<ul
			class="items"
			{{didInsert this.setupView}}
			{{onWindowEvents this.calculateItemsHeight "scroll resize orientationchange" 300}}
			{{willDestroy this.teardownView}}
		>
			{{#each @options as |item|}}
				<li
					role="button"
					class={{joinClasses
						(array
							(if item.isTemporaryDisabledForMetricSelectPopup "disabled")
							(if item.sourceType.isYouTube "youtube")
						)
					}}
					{{on "click" (fn @onSelect item)}}
				>
					<FormMetricSelectPopupListItemComponent
						@model={{item}}
						@isSelected={{eq item.id @metric.id}}
						@theme={{@theme}}
						@hideFavoriteBtn={{@hideFavoriteBtn}}
						@hideConnectBtn={{@hideConnectBtn}}
						@originForMetricFavorite={{@originForMetricFavorite}}
					/>
				</li>
			{{/each}}
		</ul>
	</template>
}
